/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/
import get from 'lodash/get';
import { action, computed, when } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';
import { IModuleStateProps, withModuleState } from '@msdyn365-commerce-modules/checkout-utilities';
import { AsyncResultStatus, IImageSettings, Image } from '@msdyn365-commerce/core';
import { IGiftCardExtend } from '@msdyn365-commerce/global-state';
import {
    // CardType,
    CartLine
} from '@msdyn365-commerce/retail-proxy/dist/Entities/CommerceTypes.g.d';
import { IFarmlandsCheckoutShareholderPaymentData } from './farmlands-checkout-shareholder-payment.data';
import { IFarmlandsCheckoutShareholderPaymentProps } from './farmlands-checkout-shareholder-payment.props.autogenerated';
import AddPaymentFormComponent from './components/add-payment-form';
import ErrorComponent from './components/error';
import PaymentInformationComponent from './components/payment-information';
import { isShareholder } from '../../common/method/functions';

export interface ICheckoutPaymentInstrumentModuleProps
    extends IFarmlandsCheckoutShareholderPaymentProps<IFarmlandsCheckoutShareholderPaymentData>,
        IModuleStateProps {}

interface ICheckoutPaymentInstrumentState {
    errorMessage?: string;
    isFetchingPaymentConnector?: boolean;
    isPaymentVerificationRequried?: boolean;
    paymentVerificationPostData?: string;
    isPaymentProcessing?: boolean;
}

export interface ICheckoutPaymentInstrumentViewProps
    extends IFarmlandsCheckoutShareholderPaymentProps<{}>,
        ICheckoutPaymentInstrumentState {
    className?: string;
    alert?: React.ReactNode;
    paymentInformation?: React.ReactNode;
    addPaymentForm?: React.ReactNode;
}

const iconImageSettings: IImageSettings = {
    viewports: {
        xs: { q: '', w: 24, h: 16 }
    },
    lazyload: true
};

export const SHAREHOLDER_KEY_VALUE: string = 'isShareholderPayment';

/**
 *
 * CheckoutShareholderPayment component
 * @extends {React.Component<IFarmlandsCheckoutShareholderPaymentProps<IFarmlandsCheckoutShareholderPaymentData>>}
 */
// @ts-ignore
@withModuleState
@observer
export class CheckoutShareholderPayment extends React.Component<ICheckoutPaymentInstrumentModuleProps> {
    @computed public get displayName(): Readonly<string | undefined> {
        const customer = this.props.data.accountInformation?.result;
        return customer ? customer.FirstName || customer.Name : undefined;
    }

    @computed get getLoyaltyAmount(): number {
        const checkoutState = this.props.data.checkout.result;
        return checkoutState && checkoutState.loyaltyAmount ? checkoutState.loyaltyAmount : 0;
    }

    @computed get getGiftCardTotalAmount(): number {
        const checkoutState = this.props.data.checkout.result;
        if (!checkoutState || !checkoutState.giftCardExtends) {
            return 0;
        }
        return checkoutState.giftCardExtends.reduce((count: number, giftCard: IGiftCardExtend) => {
            const balance: number = giftCard.Balance || 0;
            return count + balance;
            // tslint:disable-next-line:align
        }, 0);
    }

    @computed get shouldPaidByCard(): boolean {
        const cart = this.props.data.checkout.result ? this.props.data.checkout.result.checkoutCart.cart : undefined;
        if (!cart || !cart.CartLines || !cart.CartLines.length) {
            return false;
        }
        // Use payment card when it is not free or gift card balance can not cover the total amount
        const amountDue = (cart.TotalAmount || 0) - this.getGiftCardTotalAmount - this.getLoyaltyAmount;
        return amountDue > 0;
    }

    @computed get isCartContainsItemsForShipping(): boolean {
        const pickupDeliveryModeCode = get(this.props, 'context.request.channel.PickupDeliveryModeCode');
        return (get(this.props, 'data.checkout.result.checkoutCart.cart.CartLines') || []).some(
            (cartLine: CartLine) => cartLine.DeliveryMode !== pickupDeliveryModeCode
        );
    }

    @computed get isCartHasSelectedDeliveryMethods(): boolean {
        return (get(this.props, 'data.checkout.result.checkoutCart.cart.CartLines') || []).every(
            (cartLine: CartLine) => !!cartLine.DeliveryMode
        );
    }

    @computed get asyncResultStatus(): AsyncResultStatus | undefined {
        const isLoading = Object.values(this.props.data).some((data) => get(data, 'status') === 'LOADING');
        if (isLoading) {
            return 'LOADING';
        }

        const isSuccess = Object.values(this.props.data).every((data) => get(data, 'status') === 'SUCCESS');
        if (isSuccess) {
            return 'SUCCESS';
        }

        const isFailed = Object.values(this.props.data).some((data) => get(data, 'status') === 'FAILED');
        if (isFailed) {
            return 'FAILED';
        }

        return;
    }

    @computed get isDataReady(): boolean {
        return (this.props.data.checkout.result && this.props.data.checkout.status) === 'SUCCESS';
    }

    public state: ICheckoutPaymentInstrumentState = {
        isFetchingPaymentConnector: true
    };

    public componentDidMount(): void {
        when(
            () => this.asyncResultStatus !== 'LOADING',
            () => {
                this.init();
            }
        );
    }

    // tslint:disable-next-line:cyclomatic-complexity
    public render(): JSX.Element | null {
        const { errorMessage, isPaymentVerificationRequried, isPaymentProcessing } = this.state;
        const {
            moduleState: { isReady, hasError, hasInitialized, hasExternalSubmitGroup },
            config: { className, showBillingAddress, inputLabel, paragraph, title, image, onHoldMessage, suspendedMessage },
            context,
            resources,
            data: { checkout }
        } = this.props;

        if (!this.isEnabled()) {
            return null;
        }

        if (!hasInitialized || (this.asyncResultStatus !== 'FAILED' && !this.shouldPaidByCard)) {
            context.telemetry.error('Checkout payment content is empty, module wont render');
            return null;
        }

        const imageProps = {
            gridSettings: this.props.context.request.gridSettings || {},
            imageSettings: image && iconImageSettings
        };
        const contentBlockImage = image && <Image {...image} {...imageProps} />;

        const { tokenizedPaymentCard, tenderLine, billingAddress } = checkout.result || {
            tokenizedPaymentCard: undefined,
            tenderLine: undefined,
            billingAddress: undefined
        };

        const viewProps: ICheckoutPaymentInstrumentViewProps = {
            ...this.props,
            ...this.state,
            className,
            alert: hasError && errorMessage && <ErrorComponent {...{ title: resources.errorMessageTitle, message: errorMessage }} />,
            paymentInformation: isReady && !isPaymentVerificationRequried && !isPaymentProcessing && (
                <PaymentInformationComponent
                    tokenizedPaymentCard={tokenizedPaymentCard}
                    tenderLine={tenderLine}
                    billingAddress={showBillingAddress ? billingAddress : undefined}
                    canEdit={!hasExternalSubmitGroup}
                    onEdit={this.onEdit}
                    resources={resources}
                />
            ),
            addPaymentForm: (!isReady || isPaymentVerificationRequried) && !isPaymentProcessing && (
                <AddPaymentFormComponent
                    onSubmit={this.onSubmit}
                    onCancel={this.onCancel}
                    canSubmit={!hasExternalSubmitGroup}
                    canCancel={!hasExternalSubmitGroup}
                    resources={resources}
                    text={paragraph || ''}
                    inputLabel={inputLabel || ''}
                    cart={checkout.result?.checkoutCart.cart!}
                    title={title || ''}
                    image={contentBlockImage}
                    onHoldMessage={onHoldMessage || ''}
                    suspendedMessage={suspendedMessage || ''}
                    accountName={this.displayName}
                />
            )
        };

        return this.props.renderView(viewProps) as React.ReactElement;
    }

    @action
    private isEnabled = () => {
        return isShareholder(this.props.data.checkout.result?.checkoutCart.cart!);
    };

    @action
    private init = (): void => {
        this.props.moduleState.init({
            onEdit: this.onEdit,
            onCancel: this.onCancel,
            onSubmit: this.onSubmit,
            isCancellable: false,
            isRequired: false
        });
    };

    private saveExtensionProp = () => {
        const cartProperties = this.props.data.checkout.result?.checkoutCart.cart!.ExtensionProperties!;
        if (!cartProperties.find((prop) => prop.Key === SHAREHOLDER_KEY_VALUE)) {
            cartProperties.push({
                Key: SHAREHOLDER_KEY_VALUE,
                Value: {
                    BooleanValue: true
                }
            });
        }
        this.props.moduleState.onReady();
    };

    @action
    private onSubmit = async (): Promise<void> => {
        this.saveExtensionProp();
    };

    @action
    private onCancel = (): void => {
        this.props.moduleState.onReady();
    };

    @action
    private onEdit = (): void => {
        this.props.moduleState.onUpdating();
    };
}

export default CheckoutShareholderPayment;
